import React, { useState, useEffect, useCallback } from "react";
import styles from "./FiscalDeviceCommentsScreen.module.scss";
import classNames from "classnames";
import commentsService from "../../../../apis/services/admin/commentsService";
import { toast } from "react-toastify";
import BaseModal from "../../../../components/modals/baseModal/BaseModal";
import moment from "moment";

// Comment type configuration
const TYPE_CONFIG = {
  COMMENT: { label: "Komentar", icon: "💬", color: "#6B7280" },
  NOTE: { label: "Beleška", icon: "📝", color: "#3B82F6" },
  SERVICE_RECORD: { label: "Servis", icon: "🔧", color: "#8B5CF6" },
  ISSUE: { label: "Problem", icon: "⚠️", color: "#EF4444" },
  RESOLUTION: { label: "Rešenje", icon: "✅", color: "#22C55E" },
};

const COMMENT_TYPES = Object.keys(TYPE_CONFIG);

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = moment(dateString);
  const now = moment();
  
  if (now.diff(date, "hours") < 24) {
    return date.fromNow();
  }
  return date.format("DD.MM.YYYY HH:mm");
};

const FiscalDeviceCommentsScreen = ({ id }) => {
  const [comments, setComments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 20;
  
  // Filter
  const [selectedType, setSelectedType] = useState(null);
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  
  // Form states
  const [formType, setFormType] = useState("COMMENT");
  const [formContent, setFormContent] = useState("");
  const [formIsPinned, setFormIsPinned] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Fetch comments
  const fetchComments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, perPage };
      if (selectedType) params.type = selectedType;
      
      const response = await commentsService.fetchComments(id, params);
      // Response is already unwrapped: { data: [...], perPage, page, total }
      setComments(response?.data || []);
      setTotalPages(Math.ceil((response?.total || 0) / perPage));
    } catch (err) {
      console.error("Error fetching comments:", err);
      setError("Greška pri učitavanju komentara");
    } finally {
      setLoading(false);
    }
  }, [id, page, perPage, selectedType]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await commentsService.fetchCommentStats(id);
      // Response is already unwrapped: { total, pinned, byType }
      setStats(response);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, [id]);

  useEffect(() => {
    fetchComments();
    fetchStats();
  }, [fetchComments, fetchStats]);

  // Open modal for creating new comment
  const handleCreateClick = () => {
    setEditingComment(null);
    setFormType("COMMENT");
    setFormContent("");
    setFormIsPinned(false);
    setModalOpen(true);
  };

  // Open modal for editing comment
  const handleEditClick = (comment) => {
    setEditingComment(comment);
    setFormType(comment.type);
    setFormContent(comment.content);
    setFormIsPinned(comment.isPinned);
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingComment(null);
    setFormType("COMMENT");
    setFormContent("");
    setFormIsPinned(false);
  };

  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formContent.trim()) {
      toast.error("Sadržaj je obavezan");
      return;
    }

    setFormSubmitting(true);
    try {
      const data = {
        type: formType,
        content: formContent.trim(),
        isPinned: formIsPinned,
      };

      if (editingComment) {
        await commentsService.updateComment(editingComment.id, data);
        toast.success("Komentar uspešno ažuriran");
      } else {
        await commentsService.createComment(id, data);
        toast.success("Komentar uspešno dodat");
      }

      handleCloseModal();
      fetchComments();
      fetchStats();
    } catch (err) {
      console.error("Error saving comment:", err);
      toast.error(editingComment ? "Greška pri ažuriranju komentara" : "Greška pri dodavanju komentara");
    } finally {
      setFormSubmitting(false);
    }
  };

  // Open delete confirmation
  const handleDeleteClick = (comment) => {
    setCommentToDelete(comment);
    setDeleteModalOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!commentToDelete) return;

    try {
      await commentsService.deleteComment(commentToDelete.id);
      toast.success("Komentar uspešno obrisan");
      setDeleteModalOpen(false);
      setCommentToDelete(null);
      fetchComments();
      fetchStats();
    } catch (err) {
      console.error("Error deleting comment:", err);
      toast.error("Greška pri brisanju komentara");
    }
  };

  // Toggle pin
  const handleTogglePin = async (comment) => {
    try {
      await commentsService.togglePin(comment.id);
      toast.success(comment.isPinned ? "Komentar otkačen" : "Komentar zakačen");
      fetchComments();
      fetchStats();
    } catch (err) {
      console.error("Error toggling pin:", err);
      toast.error("Greška pri zakačivanju komentara");
    }
  };

  // Handle type filter change
  const handleTypeFilter = (type) => {
    setSelectedType(type === selectedType ? null : type);
    setPage(1);
  };

  // Render loading state
  const renderLoading = () => (
    <div className={styles.loadingState}>
      <div className={styles.spinner} />
      <p>Učitavanje...</p>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className={styles.errorState}>
      <p>⚠️ {error}</p>
      <button className={styles.retryButton} onClick={fetchComments}>
        Pokušaj ponovo
      </button>
    </div>
  );

  // Render empty state
  const renderEmpty = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>💬</div>
      <p>Nema komentara</p>
      <button className={styles.createButton} onClick={handleCreateClick}>
        Dodaj prvi komentar
      </button>
    </div>
  );

  // Render comment card
  const renderCommentCard = (comment) => (
    <div
      key={comment.id}
      className={classNames(
        styles.commentCard,
        comment.isPinned && styles.commentCardPinned
      )}
    >
      <div className={styles.commentHeader}>
        <div className={styles.commentHeaderLeft}>
          <span
            className={styles.typeBadge}
            style={{ backgroundColor: TYPE_CONFIG[comment.type]?.color }}
          >
            {TYPE_CONFIG[comment.type]?.icon} {TYPE_CONFIG[comment.type]?.label}
          </span>
          {comment.isPinned && (
            <span className={styles.pinnedBadge}>📌 Zakačeno</span>
          )}
        </div>
        <span className={styles.commentDate}>{formatDate(comment.createdAt)}</span>
      </div>
      
      <div className={styles.commentContent}>
        {comment.content}
      </div>
      
      <div className={styles.commentFooter}>
        <span className={styles.commentAuthor}>
          {comment.user?.firstName} {comment.user?.lastName}
        </span>
        <div className={styles.commentActions}>
          <button
            className={classNames(styles.actionButton, comment.isPinned && styles.actionButtonActive)}
            onClick={() => handleTogglePin(comment)}
            title={comment.isPinned ? "Otkači" : "Zakači"}
          >
            📌
          </button>
          <button
            className={styles.actionButton}
            onClick={() => handleEditClick(comment)}
            title="Izmeni"
          >
            ✏️
          </button>
          <button
            className={classNames(styles.actionButton, styles.actionButtonDanger)}
            onClick={() => handleDeleteClick(comment)}
            title="Obriši"
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  );

  // Render stats tabs
  const renderStatsTabs = () => (
    <div className={styles.statsTabs}>
      <button
        className={classNames(
          styles.statsTab,
          !selectedType && styles.statsTabActive
        )}
        onClick={() => handleTypeFilter(null)}
      >
        Svi ({stats?.total || 0})
      </button>
      {COMMENT_TYPES.map((type) => (
        <button
          key={type}
          className={classNames(
            styles.statsTab,
            selectedType === type && styles.statsTabActive
          )}
          style={{
            borderColor: selectedType === type ? TYPE_CONFIG[type].color : "transparent",
          }}
          onClick={() => handleTypeFilter(type)}
        >
          {TYPE_CONFIG[type].icon} {stats?.byType?.[type] || 0}
        </button>
      ))}
    </div>
  );

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className={styles.pagination}>
        <button
          className={styles.paginationButton}
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          ← Prethodna
        </button>
        <span className={styles.paginationInfo}>
          Strana {page} od {totalPages}
        </span>
        <button
          className={styles.paginationButton}
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
        >
          Sledeća →
        </button>
      </div>
    );
  };

  return (
    <div className={styles.content}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Komentari</h2>
        <button className={styles.createButton} onClick={handleCreateClick}>
          + Novi komentar
        </button>
      </div>

      {/* Stats/Filter Tabs */}
      {stats && renderStatsTabs()}

      {/* Content */}
      <div className={styles.commentsContainer}>
        {loading && renderLoading()}
        {error && !loading && renderError()}
        {!loading && !error && comments.length === 0 && renderEmpty()}
        {!loading && !error && comments.length > 0 && (
          <>
            <div className={styles.commentsList}>
              {comments.map(renderCommentCard)}
            </div>
            {renderPagination()}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      <BaseModal
        modalOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingComment ? "Izmeni komentar" : "Novi komentar"}
      >
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Tip</label>
            <div className={styles.typeSelector}>
              {COMMENT_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={classNames(
                    styles.typeOption,
                    formType === type && styles.typeOptionSelected
                  )}
                  style={{
                    borderColor: formType === type ? TYPE_CONFIG[type].color : "#ddd",
                    backgroundColor: formType === type ? TYPE_CONFIG[type].color : "#fff",
                    color: formType === type ? "#fff" : "#333",
                  }}
                  onClick={() => setFormType(type)}
                >
                  {TYPE_CONFIG[type].icon} {TYPE_CONFIG[type].label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Sadržaj *</label>
            <textarea
              className={styles.textarea}
              value={formContent}
              onChange={(e) => setFormContent(e.target.value)}
              placeholder="Unesite komentar..."
              rows={5}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formIsPinned}
                onChange={(e) => setFormIsPinned(e.target.checked)}
              />
              <span>📌 Zakači na vrh</span>
            </label>
          </div>

          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCloseModal}
              disabled={formSubmitting}
            >
              Otkaži
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={formSubmitting || !formContent.trim()}
            >
              {formSubmitting ? "Čuvanje..." : editingComment ? "Sačuvaj" : "Dodaj"}
            </button>
          </div>
        </form>
      </BaseModal>

      {/* Delete Confirmation Modal */}
      <BaseModal
        modalOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setCommentToDelete(null);
        }}
        title="Potvrda brisanja"
      >
        <div className={styles.deleteConfirmation}>
          <p>Da li ste sigurni da želite da obrišete ovaj komentar?</p>
          {commentToDelete && (
            <div className={styles.deletePreview}>
              <span
                className={styles.typeBadge}
                style={{ backgroundColor: TYPE_CONFIG[commentToDelete.type]?.color }}
              >
                {TYPE_CONFIG[commentToDelete.type]?.icon} {TYPE_CONFIG[commentToDelete.type]?.label}
              </span>
              <p className={styles.deletePreviewContent}>
                {commentToDelete.content.length > 100
                  ? commentToDelete.content.substring(0, 100) + "..."
                  : commentToDelete.content}
              </p>
            </div>
          )}
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => {
                setDeleteModalOpen(false);
                setCommentToDelete(null);
              }}
            >
              Otkaži
            </button>
            <button
              type="button"
              className={styles.deleteConfirmButton}
              onClick={handleConfirmDelete}
            >
              Obriši
            </button>
          </div>
        </div>
      </BaseModal>
    </div>
  );
};

export default FiscalDeviceCommentsScreen;

